import { useState, type ReactNode } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Role } from '@crm/shared';
import { useAuth } from '../features/auth/AuthContext';
import { NotificationBell } from '../features/notifications/NotificationBell';
import { useOverdueCount } from '../features/appointments/api';

type NavItem = {
  to: string;
  labelKey: string;
  enabled: boolean;
  roles?: Role[]; // jeśli ustawione — pokazuj tylko tym rolom
  /**
   * Faza 19 — jeśli ustawione, link renderuje obok labela czerwoną kropkę
   * z liczbą zaległych wizyt. Wartość pobierana z `useOverdueCount` (polling
   * 60 s). Trzymane przez slug, żeby nie wstrzykiwać hooka w arrayu.
   */
  badge?: 'overdue';
};

const NAV_ITEMS: NavItem[] = [
  { to: '/', labelKey: 'nav.dashboard', enabled: true },
  { to: '/appointments', labelKey: 'nav.appointments', enabled: true },
  { to: '/appointments/overdue', labelKey: 'nav.overdue', enabled: true, badge: 'overdue' },
  { to: '/clients', labelKey: 'nav.clients', enabled: true },
  { to: '/employees', labelKey: 'nav.employees', enabled: true, roles: ['ADMIN'] },
  { to: '/services', labelKey: 'nav.services', enabled: true, roles: ['ADMIN'] },
  { to: '/schedules', labelKey: 'nav.schedules', enabled: true },
  { to: '/settings/notifications', labelKey: 'nav.settingsNotifications', enabled: true, roles: ['ADMIN'] },
  { to: '/reports', labelKey: 'nav.reports', enabled: true, roles: ['ADMIN'] },
  {
    to: '/commissions/rules',
    labelKey: 'nav.commissionsRules',
    enabled: true,
    roles: ['ADMIN'],
  },
  {
    to: '/commissions/payouts',
    labelKey: 'nav.commissionsPayouts',
    enabled: true,
    roles: ['ADMIN', 'EMPLOYEE'],
  },
  { to: '/portfolio', labelKey: 'nav.portfolio', enabled: true, roles: ['ADMIN', 'EMPLOYEE'] },
];

export function DashboardLayout(): ReactNode {
  const [collapsed, setCollapsed] = useState(false);
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const overdueCount = useOverdueCount().data?.count ?? 0;

  const initials = user
    ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase()
    : '?';

  return (
    <div className="flex h-full bg-zinc-50">
      <aside
        className={`flex flex-col border-r border-zinc-200 bg-white transition-all duration-200 ${
          collapsed ? 'w-16' : 'w-60'
        }`}
      >
        <div className="flex h-14 items-center justify-between px-3">
          {!collapsed && (
            <span className="truncate text-sm font-semibold text-brand-700">
              {t('app.title')}
            </span>
          )}
          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            className="rounded-md p-2 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
            aria-label="toggle sidebar"
          >
            {collapsed ? '›' : '‹'}
          </button>
        </div>

        <nav className="flex-1 space-y-1 px-2 py-2">
          {NAV_ITEMS.filter((item) => !item.roles || (user && item.roles.includes(user.role))).map((item) => {
            const label = t(item.labelKey);
            if (!item.enabled) {
              return (
                <div
                  key={item.to}
                  className="flex cursor-not-allowed items-center gap-3 rounded-md px-3 py-2 text-sm text-zinc-400"
                  title={t('nav.comingSoon')}
                >
                  <span className="inline-block h-2 w-2 rounded-full bg-zinc-300" />
                  {!collapsed && (
                    <span className="flex-1 truncate">
                      {label}
                      <span className="ml-1 text-[10px] uppercase text-zinc-400">
                        ({t('nav.comingSoon')})
                      </span>
                    </span>
                  )}
                </div>
              );
            }
            const badgeCount = item.badge === 'overdue' ? overdueCount : 0;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end
                className={({ isActive }) =>
                  `relative flex items-center gap-3 rounded-md px-3 py-2 text-sm transition ${
                    isActive
                      ? 'bg-brand-50 text-brand-700'
                      : 'text-zinc-700 hover:bg-zinc-100 hover:text-zinc-900'
                  }`
                }
              >
                <span
                  className={`inline-block h-2 w-2 rounded-full ${
                    badgeCount > 0 ? 'bg-red-500' : 'bg-brand-500'
                  }`}
                />
                {!collapsed && (
                  <>
                    <span className="flex-1 truncate">{label}</span>
                    {badgeCount > 0 && (
                      <span className="ml-2 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-600 px-1.5 text-[11px] font-semibold text-white">
                        {badgeCount > 99 ? '99+' : badgeCount}
                      </span>
                    )}
                  </>
                )}
                {collapsed && badgeCount > 0 && (
                  <span
                    aria-label={t('appointments.overdue.title') ?? ''}
                    className="absolute right-1 top-1 inline-flex h-2 w-2 rounded-full bg-red-600"
                  />
                )}
              </NavLink>
            );
          })}
        </nav>
      </aside>

      <div className="flex flex-1 flex-col">
        <header className="flex h-14 items-center justify-between border-b border-zinc-200 bg-white px-6">
          <div className="text-sm text-zinc-500">{t('app.title')}</div>

          <div className="flex items-center gap-4">
            <NotificationBell />

            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-100 text-sm font-semibold text-brand-700">
                {initials}
              </div>
              {user && (
                <div className="hidden text-right text-sm sm:block">
                  <div className="font-medium text-zinc-900">
                    {user.firstName} {user.lastName}
                  </div>
                  <div className="text-xs text-zinc-500">
                    {t(`roles.${user.role}` as const)}
                  </div>
                </div>
              )}
              <button
                type="button"
                onClick={() => void logout()}
                className="rounded-md border border-zinc-300 bg-white px-3 py-1.5 text-sm text-zinc-700 transition hover:bg-zinc-50"
              >
                {t('auth.logout')}
              </button>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
